import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";
import {
  deleteModulePayment,
  modifyModulePayment,
  refundModulePayment,
} from "views/admin/Accounts/verification/modulePaymentVerificationApi";

const ACTION_META = {
  modify: { title: "Modify payment", confirmLabel: "Save changes" },
  refund: { title: "Refund payment", confirmLabel: "Submit refund" },
  delete: { title: "Delete payment", confirmLabel: "Delete payment" },
};

const getApiError = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

/** Shared modify/refund/delete dialog for a verified module payment (Visa, Insurance, RPL, SkillAssessment). */
const ModulePaymentActionModal = ({
  open,
  action,
  payment,
  refundableAvailable = null,
  invalidateQueryKey,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [autoVerify, setAutoVerify] = useState(false);
  const canSelfVerify = hasPermission(P.ACCOUNTS.PAYMENT_VERIFY);

  const meta = action ? ACTION_META[action] : null;

  const mutation = useMutation({
    mutationFn: () => {
      if (action === "modify") return modifyModulePayment(payment.id, { amount, reason });
      if (action === "refund") {
        return refundModulePayment(payment.id, {
          amount,
          reason,
          notes: reason,
          autoVerify: canSelfVerify && autoVerify,
        });
      }
      return deleteModulePayment(payment.id, reason);
    },
    onSuccess: () => {
      toast.success(
        action === "refund" && !(canSelfVerify && autoVerify)
          ? "Refund submitted for verification"
          : `${meta.title} successful`
      );
      if (invalidateQueryKey) {
        queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      }
      onSuccess?.();
      handleClose();
    },
    onError: (err) => toast.error(getApiError(err, "Action failed")),
  });

  const handleClose = () => {
    if (mutation.isLoading) return;
    setAmount("");
    setReason("");
    setAutoVerify(false);
    onClose();
  };

  if (!open || !payment || !meta) return null;

  const refundAmount = Number(amount);
  const refundExceedsCap =
    action === "refund" &&
    refundableAvailable != null &&
    refundAmount > 0 &&
    refundAmount > refundableAvailable;

  const canSubmit =
    reason.trim().length > 0 && (action === "delete" || (Number(amount) > 0 && !refundExceedsCap));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{meta.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {action === "refund" && refundableAvailable != null && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Refundable now: ${refundableAvailable.toFixed(2)} (verified paid minus pending
              refunds). Verifying refunds cannot exceed this total.
            </Alert>
          )}
          {action !== "delete" && (
            <TextField
              label={action === "refund" ? "Refund amount (AUD)" : "New amount"}
              type="number"
              size="small"
              required
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{
                min: 0,
                step: "0.01",
                max:
                  action === "refund" && refundableAvailable != null
                    ? refundableAvailable
                    : undefined,
              }}
              error={refundExceedsCap}
              helperText={
                refundExceedsCap
                  ? `Cannot exceed refundable amount ($${refundableAvailable.toFixed(2)})`
                  : undefined
              }
            />
          )}
          <TextField
            label="Reason"
            size="small"
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why — shown in the audit note"
          />
          {action === "refund" && canSelfVerify && (
            <FormControlLabel
              control={
                <Checkbox checked={autoVerify} onChange={(e) => setAutoVerify(e.target.checked)} />
              }
              label={<Typography variant="body2">Verify immediately</Typography>}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={action === "delete" ? "error" : "primary"}
          disabled={!canSubmit || mutation.isLoading}
          onClick={() => mutation.mutate()}
        >
          {mutation.isLoading ? "Working…" : meta.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModulePaymentActionModal;
